import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Eye, QrCode, IndianRupee, ShoppingBag, TrendingUp } from "lucide-react";

export const RestaurantAnalytics = () => {
  const [data, setData] = useState({ qr_scans: 0, menu_views: 0, daily: [], popular_items: [], revenue: null });

  useEffect(() => { api.get("/restaurant/analytics").then(({ data }) => setData(data)); }, []);

  const dailyByDate = {};
  data.daily.forEach((d) => {
    if (!dailyByDate[d.date]) dailyByDate[d.date] = { date: d.date, qr_scan: 0, menu_view: 0 };
    dailyByDate[d.date][d.type] = d.count;
  });
  const dailyRows = Object.values(dailyByDate).sort((a, b) => a.date.localeCompare(b.date));

  const rev = data.revenue || { total_revenue: 0, total_orders: 0, commission_saved: 0, avg_order_value: 0, monthly: [] };

  return (
    <div data-testid="rest-analytics">
      <h1 className="text-2xl font-bold">Analytics</h1>
      <p className="text-sm text-zq-muted mt-1">Scans, views, orders & commission saved.</p>

      {/* Revenue strip */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <div className="zq-card p-5 border-zq-orange border-2" data-testid="rev-commission">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zq-orange font-semibold"><TrendingUp size={14}/> Commission saved</div>
          <div className="text-2xl font-bold mt-2">{inr(rev.commission_saved)}</div>
          <div className="text-xs text-zq-muted mt-1">vs 25% aggregator commission</div>
        </div>
        <div className="zq-card p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zq-muted font-semibold"><IndianRupee size={14}/> Total revenue</div>
          <div className="text-2xl font-bold mt-2">{inr(rev.total_revenue)}</div>
          <div className="text-xs text-zq-muted mt-1">via WhatsApp orders</div>
        </div>
        <div className="zq-card p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zq-muted font-semibold"><ShoppingBag size={14}/> Total orders</div>
          <div className="text-2xl font-bold mt-2">{rev.total_orders}</div>
          <div className="text-xs text-zq-muted mt-1">customers served</div>
        </div>
        <div className="zq-card p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zq-muted font-semibold">Avg. order value</div>
          <div className="text-2xl font-bold mt-2">{inr(rev.avg_order_value)}</div>
          <div className="text-xs text-zq-muted mt-1">per WhatsApp order</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div className="zq-card p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-md bg-zq-orange-light text-zq-orange flex items-center justify-center"><QrCode size={20}/></div>
          <div><div className="text-2xl font-bold">{data.qr_scans}</div><div className="text-sm text-zq-muted">Total QR scans</div></div>
        </div>
        <div className="zq-card p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-md bg-zq-orange-light text-zq-orange flex items-center justify-center"><Eye size={20}/></div>
          <div><div className="text-2xl font-bold">{data.menu_views}</div><div className="text-sm text-zq-muted">Total menu views</div></div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-6">
        <div className="zq-card p-5">
          <h3 className="font-semibold">Monthly revenue & savings</h3>
          {rev.monthly.length === 0 ? (
            <p className="text-sm text-zq-muted mt-3">No orders yet.</p>
          ) : (
            <table className="w-full mt-3 text-sm">
              <thead className="text-xs text-zq-muted">
                <tr>
                  <th className="text-left p-2">Month</th>
                  <th className="text-left p-2">Orders</th>
                  <th className="text-left p-2">Revenue</th>
                  <th className="text-left p-2">Saved</th>
                </tr>
              </thead>
              <tbody>
                {rev.monthly.map((m) => (
                  <tr key={m.month} className="border-t border-zq-border">
                    <td className="p-2">{m.month}</td>
                    <td className="p-2">{m.orders}</td>
                    <td className="p-2">{inr(m.revenue)}</td>
                    <td className="p-2 text-zq-orange font-semibold">{inr(m.commission_saved)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="zq-card p-5">
          <h3 className="font-semibold">Daily activity</h3>
          {dailyRows.length === 0 ? (
            <p className="text-sm text-zq-muted mt-3">No activity yet.</p>
          ) : (
            <table className="w-full mt-3 text-sm">
              <thead className="text-xs text-zq-muted">
                <tr><th className="text-left p-2">Date</th><th className="text-left p-2">Scans</th><th className="text-left p-2">Views</th></tr>
              </thead>
              <tbody>
                {dailyRows.map((r) => (
                  <tr key={r.date} className="border-t border-zq-border">
                    <td className="p-2">{r.date}</td>
                    <td className="p-2">{r.qr_scan || 0}</td>
                    <td className="p-2">{r.menu_view || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="zq-card p-5 md:col-span-2">
          <h3 className="font-semibold">Popular items</h3>
          {data.popular_items.length === 0 ? (
            <p className="text-sm text-zq-muted mt-3">Add menu items and start receiving views.</p>
          ) : (
            <ol className="mt-3 space-y-2">
              {data.popular_items.map((it, i) => (
                <li key={it.id} className="flex items-center justify-between text-sm">
                  <span><span className="text-zq-orange font-bold mr-2">#{i + 1}</span>{it.name?.en || it.name}</span>
                  <span className="zq-pill zq-pill-orange">{it.views} views</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};
